import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  ShieldCheck,
} from 'lucide-react';

import './ControlCenterLogin.css';

const API_BASE_URL = window.location.origin;

const ControlCenterLogin = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const email = formData.email.trim();
    const password = formData.password;

    if (!email || !password) {
      setError('Email aur password dono enter karein.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${API_BASE_URL}/api/control-center/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.message ||
            'Admin login failed. Credentials check karein.'
        );
      }

      if (!data.token) {
        throw new Error('Login token backend se receive nahi hua.');
      }

      const storage = rememberMe ? localStorage : sessionStorage;

      localStorage.removeItem('vakra_control_admin_token');
      sessionStorage.removeItem('vakra_control_admin_token');

      localStorage.removeItem('vakra_control_admin');
      sessionStorage.removeItem('vakra_control_admin');

      storage.setItem(
        'vakra_control_admin_token',
        data.token
      );

      if (data.admin) {
        storage.setItem(
          'vakra_control_admin',
          JSON.stringify(data.admin)
        );
      }

      // Login successful hone ke baad Vendors page
      navigate('/control-center/vendors', {
        replace: true,
      });
    } catch (loginError) {
      setError(
        loginError.message ||
          'Login nahi ho paya. Dobara try karein.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vcc-login-page">
      <div className="vcc-login-glow vcc-login-glow-one" />
      <div className="vcc-login-glow vcc-login-glow-two" />

      <button
        type="button"
        className="vcc-login-back"
        onClick={() => navigate('/control-center')}
      >
        <ArrowLeft size={18} />
        Back
      </button>

      <main className="vcc-login-container">
        <section className="vcc-login-brand-panel">
          <div className="vcc-login-brand">
            <div className="vcc-login-logo">V</div>

            <div>
              <strong>VAKRA</strong>
              <span>CONTROL CENTER</span>
            </div>
          </div>

          <div className="vcc-login-brand-content">
            <div className="vcc-login-secure-badge">
              <ShieldCheck size={18} />
              Authorized access only
            </div>

            <h1>
              Securely manage the complete
              <span> VAKRA ecosystem.</span>
            </h1>

            <p>
              Vendors, subscriptions, invoices, payments,
              smart campaigns and analytics ko ek central
              platform se manage karein.
            </p>
          </div>

          <div className="vcc-login-security-card">
            <LockKeyhole size={22} />

            <div>
              <strong>Protected Admin System</strong>
              <span>
                Valid admin credentials ke bina access allowed
                nahi hai.
              </span>
            </div>
          </div>
        </section>

        <section className="vcc-login-form-panel">
          <div className="vcc-login-form-wrapper">
            <div className="vcc-login-form-heading">
              <span>ADMIN PORTAL</span>
              <h2>Welcome back</h2>

              <p>
                Control Center access karne ke liye login
                karein.
              </p>
            </div>

            <form
              className="vcc-login-form"
              onSubmit={handleSubmit}
              noValidate
            >
              {error && (
                <div className="vcc-login-error">
                  {error}
                </div>
              )}

              <label className="vcc-login-field">
                <span>Email address</span>

                <div className="vcc-login-input-box">
                  <Mail size={19} />

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="admin@vakra.co.in"
                    autoComplete="email"
                    disabled={loading}
                  />
                </div>
              </label>

              <label className="vcc-login-field">
                <span>Password</span>

                <div className="vcc-login-input-box">
                  <LockKeyhole size={19} />

                  <input
                    type={
                      showPassword ? 'text' : 'password'
                    }
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={loading}
                  />

                  <button
                    type="button"
                    className="vcc-password-button"
                    onClick={() =>
                      setShowPassword(
                        (previous) => !previous
                      )
                    }
                    aria-label={
                      showPassword
                        ? 'Hide password'
                        : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <EyeOff size={19} />
                    ) : (
                      <Eye size={19} />
                    )}
                  </button>
                </div>
              </label>

              <div className="vcc-login-options">
                <label className="vcc-remember-option">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) =>
                      setRememberMe(
                        event.target.checked
                      )
                    }
                    disabled={loading}
                  />

                  <span>Keep me signed in</span>
                </label>

                <button
                  type="button"
                  className="vcc-forgot-button"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                className="vcc-login-submit"
                disabled={loading}
              >
                {loading
                  ? 'Signing in...'
                  : 'Login to Control Center'}

                {!loading && (
                  <ArrowRight size={20} />
                )}
              </button>
            </form>

            <p className="vcc-login-help">
              Login problem ho to VAKRA system
              administrator se contact karein.
            </p>
          </div>
        </section>
      </main>

      <footer className="vcc-login-footer">
        © {new Date().getFullYear()} VAKRA. Secure
        Control Center.
      </footer>
    </div>
  );
};

export default ControlCenterLogin;
